import { TFunction } from "i18next";
import moment from "moment";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Column,
  Row,
  Spacer,
  TwoColGrid,
} from "../../components/Layout/Layout";
import { getCameraStatus, saveCamera } from "../../core/api/cameras/cameras";
import {
  CameraConnectionStatus,
  CameraStatus,
  EditCameraModel,
} from "../../core/api/cameras/types";
import { CameraProvisioning } from "../../core/enumerations/enumerations";
import {
  getNonEmptyProps,
  isEmpty,
  notifyApiErrors,
} from "../../core/helpers/helpers";
import { validateIPAddress } from "../../core/helpers/validation";
import useTranslations from "../../core/i18n/useTranslations";
import {
  useObjectForm,
  useObjectFormRef,
} from "../../core/SaveObjectContext/hooks";
import useUser from "../../core/user/useUser";
import Alert from "../../ui-lib/components/Alerts/Alert";
import { notify } from "../../ui-lib/components/Alerts/Toast";
import Badge from "../../ui-lib/components/Badges/Badge";
import Button from "../../ui-lib/components/Button/Button";
import Checkbox from "../../ui-lib/components/Checkbox/Checkbox";
import Divider from "../../ui-lib/components/Divider/Divider";
import Dropdown from "../../ui-lib/components/Dropdown/Dropdown";
import { IDropdownItem } from "../../ui-lib/components/Dropdown/DropdownItem";
import inputStyles from "../../ui-lib/components/Inputs/Input.module.css";
import InputContainer from "../../ui-lib/components/Inputs/InputContainer";
import NumberStepInput from "../../ui-lib/components/Inputs/NumberStepInput";
import TextInput from "../../ui-lib/components/Inputs/TextInput";
import ToggleSwitch from "../../ui-lib/components/Checkbox/ToggleSwitch";
import { Role, RoleFeature } from "../RoleFeature";
import {
  ITreeData,
  CustomDropdown,
} from "../../ui-lib/components/Hierarchy/CustomTreeDropdown/CustomDropdown";

export declare type EditCameraActions = {
  submitForm: () => Promise<SubmitResult>;
  reset: () => void;
};

export interface SubmitResult {
  status: boolean;
  data: number;
}

export interface EditCameraProps {
  camera?: EditCameraModel;
  isNewCamera?: boolean;
}

const cameraTypeGroups = {
  regular: [0, 2, 7],
  gateways: [3, 5],
  partnerProvidedUrl: [8, 9], // TELLU, NATTUGLA
};

const getProvisioningStatusText = (
  t: TFunction,
  provisioning?: CameraProvisioning,
): string => {
  switch (provisioning) {
    case CameraProvisioning.NotNeeded:
      return t("Cameras:provisioning_not_needed");
    case CameraProvisioning.Succeeded:
      return t("Cameras:provisioning_succeeded");
    case CameraProvisioning.InProgress:
      return t("Cameras:provisioning_in_progress");
    default:
      return "";
  }
};

const getCameraActive = (status?: CameraConnectionStatus | string): boolean => {
  if (isEmpty(status)) {
    return false;
  }
  if (status) {
    const camStatus = status as CameraConnectionStatus;
    if (camStatus && camStatus.SSH === "True") {
      return true;
    }
  }
  return false;
};

const ProvisioningStatus = ({
  provisioning,
}: {
  provisioning?: CameraProvisioning;
}) => {
  const t = useTranslations();
  const status = getProvisioningStatusText(t, provisioning);

  return (
    <Column type="top" align="fill">
      <Spacer size={16} />
      <span className="smallStrong">{status}</span>
      <Spacer size={16} />
      {provisioning === CameraProvisioning.InProgress && (
        <Alert
          variant="info"
          icon="information-circle"
          title={t("Cameras:provisioning_in_progress_description")}
        />
      )}
    </Column>
  );
};

const DeviceStatus = ({ cameraStatus }: { cameraStatus: CameraStatus }) => {
  const t = useTranslations();
  const { config } = useUser();

  return (
    <Row
      type="space"
      breakPoint={690}
      responsive
      style={{ backgroundColor: "var(--Grey-50)", padding: "8px" }}
    >
      <Row type="left" align="center">
        <span className={inputStyles.label} style={{ marginRight: "16px" }}>
          {t("Cameras:connection_status")}
        </span>
        <Badge
          variant={
            getCameraActive(cameraStatus.status) ? "success" : "secondary"
          }
          title={
            getCameraActive(cameraStatus.status)
              ? t("Cameras:status_active")
              : t("Cameras:status_inactive")
          }
        />
      </Row>
      <Row type="right" align="center">
        <p>
          <span style={{ color: "var(--Grey-600)" }}>
            {t("Cameras:last_status_update")}
          </span>{" "}
          <span style={{ color: "var(--Grey-800)" }}>
            {cameraStatus.lastStatusUpdate
              ? moment
                  .utc(cameraStatus.lastStatusUpdate)
                  .tz(config?.timezone.timezoneName ?? "")
                  .format("YYYY-MM-DD HH:mm:ss")
              : "--"}
          </span>
        </p>
      </Row>
    </Row>
  );
};

export const CameraEditForm = forwardRef<EditCameraActions, EditCameraProps>(
  (props: EditCameraProps, ref) => {
    const { camera, isNewCamera } = props;
    const t = useTranslations();
    const ojectFormRef = useObjectFormRef();
    const reactRef = useRef(null);
    const formRef = isNewCamera ? ojectFormRef : reactRef;
    const [cameraStatus, setCameraStatus] = useState<CameraStatus>();
    const { authenticatedRequest } = useUser();
    const [isRefreshingStatus, setIsRefreshingStatus] =
      useState<boolean>(false);
    const isNew = useRef<boolean>(camera === undefined);
    const [enableShortWindowTime, setEnableShortWindowTime] = useState(
      !!camera?.shortWindowTime,
    );
    const getFormModel = (): EditCameraModel => {
      const data: EditCameraModel = {
        externalPort: undefined,
        title: undefined,
        description: undefined,
        organizationId: undefined,
        cameraType: isNew.current ? 0 : (camera?.cameraType ?? 0),
        cameraAddress: undefined,
        cameraPort: undefined,
        hlsPath: undefined,
        rtmpPath: undefined,
        rtspPath: undefined,
        jpegPath: undefined,
        mjpegPath: undefined,
        webmPath: undefined,
        audioPath: undefined,
        username: null,
        password: null,
        ssl: false,
        externalId: undefined,
        shortWindowTime: 120,
        ...camera,
      };
      return data;
    };

    const customFormHook = useObjectForm<EditCameraModel>({
      defaultValues: getFormModel(),
    });
    const reactFormHook = useForm<EditCameraModel>({
      defaultValues: getFormModel(),
    });

    const {
      control,
      formState: { errors },
      handleSubmit,
      reset,
      setValue,
      getValues,
      trigger,
      watch,
    } = isNewCamera ? reactFormHook : customFormHook;
    const cameraType = watch("cameraType");
    const isPartnerProvidedUrl = useMemo(
      () => cameraTypeGroups.partnerProvidedUrl.includes(cameraType),
      [cameraType],
    );
    const isGatewayType = useMemo(
      () => cameraTypeGroups.gateways.includes(cameraType),
      [cameraType],
    );
    // Gateways ExternalId should be immutable after create
    const isExternalIdDisabled = !isNew.current && isGatewayType;
    // Optional for Tellu, required for Nattugla and gateways-on-create. Never required while disabled
    const isExternalIdRequired = cameraType !== 8 && !isExternalIdDisabled;

    const cameraTypes: IDropdownItem[] = useMemo(() => {
      return Object.values(cameraTypeGroups)
        .flat()
        .map((val) => ({
          id: val,
          name: t(`Cameras:CameraType_${val}`),
          isSelected: val === cameraType,
        }));
    }, [cameraType]);

    const isCameraProxy = cameraType === 2;

    const setCamera = async (): Promise<any> => {
      try {
        const model = getFormModel();
        reset(model);
      } catch (error: any) {
        notifyApiErrors(error.response?.data?.errors);
      }
    };

    const validateIp = (value: string): boolean => {
      const isIPAddress = validateIPAddress(value);
      if (!isIPAddress) {
        return value.includes(".") && value.indexOf(".") < value.length - 1;
      }
      return isIPAddress;
    };

    useImperativeHandle<EditCameraActions, EditCameraActions>(ref, () => {
      const actions: EditCameraActions = {
        submitForm: async (): Promise<any> => {
          let success = true;
          let data = {};
          try {
            await handleSubmit(
              async (formData: EditCameraModel): Promise<any> => {
                try {
                  const dirtyProps = getNonEmptyProps(formData);
                  const patchData: any = {};
                  dirtyProps.forEach((prop) => {
                    patchData[prop] = formData[prop];
                  });
                  const result = await saveCamera(
                    patchData,
                    authenticatedRequest,
                    formData.id,
                  );
                  data = result;

                  success = true;
                  notify({
                    message: t(
                      `Cameras:${
                        isNewCamera ? "add_success" : "update_success"
                      }`,
                    ),
                  });
                } catch (error: any) {
                  notifyApiErrors(error.response?.data?.errors);
                  success = false;
                }
              },
              () => {
                success = false;
              },
            )().catch(() => {
              success = false;
            });
          } catch (error) {
            success = false;
          }
          return { status: success, data };
        },
        reset,
      };
      return actions;
    });

    const loadStatus = async () => {
      if (camera?.id) {
        const status = await getCameraStatus(
          camera?.id?.toString(),
          authenticatedRequest,
        );
        setCameraStatus(status.data);
      }
    };

    useEffect(() => {
      setCamera();
      loadStatus();
    }, [camera]);

    useEffect(() => {
      if ((isCameraProxy && !enableShortWindowTime) || isPartnerProvidedUrl) {
        setValue("shortWindowTime", 0);
      }
    }, [cameraType, enableShortWindowTime, isPartnerProvidedUrl]);

    return (
      <form ref={formRef} className="w-100">
        <Column type="top">
          <Row type="fill" align="fill" responsive breakPoint={1040}>
            <Controller
              name="title"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <TextInput
                  required
                  inputRef={field.ref}
                  label={t("Common:name")}
                  placeholder={t("Cameras:placeholder_name")}
                  value={field.value}
                  onChange={field.onChange}
                  validationError={errors.title && t("Cameras:validation_name")}
                />
              )}
            />
            <Spacer size={16} />
            <Controller
              name="organizationId"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <CustomDropdown
                  required
                  selectionMode="single"
                  fullWidth
                  label={t("Cameras:organisation")}
                  placeholder={t("Common:select_organisation")}
                  customerFilter={{
                    customers: field.value?.toString(),
                  }}
                  onSelectSignle={(data?: ITreeData) => {
                    if (data?.key) {
                      setValue(field.name, +data.key);
                    }
                    trigger(field.name);
                  }}
                  validationError={
                    errors.organizationId && t("Cameras:organisationvalidation")
                  }
                />
              )}
            />
          </Row>
          <Spacer size={16} />
          <TwoColGrid columnGap={16}>
            <Controller
              name="cameraType"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Dropdown
                  maxVisible={7}
                  title={t("Cameras:create_camera_type")}
                  id="addcamera_type"
                  items={cameraTypes}
                  disabled={isNew.current !== true}
                  placeholder={t("Cameras:select_type")}
                  selectedItem={cameraTypes.find((r) => r.isSelected)}
                  onSelectItem={(item) => {
                    setValue(field.name, +item.id);
                    setValue("shortWindowTime", +item.id === 7 ? 60 : 120);
                    setValue("ssl", +item.id === 7);
                    trigger(field.name);
                  }}
                  invalid={errors.cameraType !== undefined}
                  validationError={
                    errors.cameraType && t("Cameras:validation_type")
                  }
                />
              )}
            />
            {!cameraTypeGroups.regular.includes(cameraType) && (
              <Controller
                name="externalId"
                control={control}
                rules={{
                  required: isExternalIdRequired,
                  maxLength: 45,
                }}
                render={({ field }) => (
                  <TextInput
                    required={isExternalIdRequired}
                    description={
                      isPartnerProvidedUrl
                        ? t(`Cameras:externalId_description_${cameraType}`)
                        : t("Cameras:externalId_description")
                    }
                    inputRef={field.ref}
                    dataCy="externalId"
                    label={t("Cameras:labels_externalid")}
                    placeholder={
                      isPartnerProvidedUrl
                        ? t(`Cameras:placeholder_externalid_${cameraType}`)
                        : t("Cameras:placeholder_externalid")
                    }
                    value={field.value}
                    onChange={field.onChange}
                    validationError={
                      errors.externalId &&
                      (errors.externalId.type === "maxLength"
                        ? t("Errors:max_chars_exceeded").replace("{0}", "45")
                        : t("Cameras:validation_externalid"))
                    }
                    disabled={isExternalIdDisabled}
                  />
                )}
              />
            )}
          </TwoColGrid>
          {isCameraProxy ? (
            <>
              <Spacer size={16} />
              <Divider />
              <Spacer size={16} />
              <Column align="start" className="w-100">
                <Row>
                  <ToggleSwitch
                    checked={enableShortWindowTime}
                    onChange={() => {
                      setEnableShortWindowTime(!enableShortWindowTime);
                      setValue("shortWindowTime", 120);
                    }}
                    name="enableShortWindowTime"
                    id="enableShortWindowTime"
                    label={t("Cameras:toggle_short_window_time")}
                    color="var(--Grey-600)"
                  />
                </Row>
                {enableShortWindowTime && (
                  <>
                    <Spacer size={16} />
                    <Row>
                      <div style={{ width: "200px" }}>
                        <Controller
                          name="shortWindowTime"
                          control={control}
                          render={({ field }) => (
                            <NumberStepInput
                              label={t("Cameras:labels_shortwindowtime")}
                              placeholder={t(
                                "Cameras:placeholder_shortwindowtime",
                              )}
                              value={field.value?.toString()}
                              onChange={field.onChange}
                              validationError={
                                errors.shortWindowTime &&
                                t("Cameras:validation_shortwindowtime")
                              }
                            />
                          )}
                        />
                      </div>
                    </Row>
                  </>
                )}
              </Column>
              <Spacer size={16} />
              <Divider />
              <Spacer size={16} />
            </>
          ) : (
            <>
              <Spacer size={16} />
              {!isPartnerProvidedUrl && (
                <>
                  <TwoColGrid>
                    <div style={{ width: "200px" }}>
                      <Controller
                        name="shortWindowTime"
                        control={control}
                        render={({ field }) => (
                          <NumberStepInput
                            label={t("Cameras:labels_shortwindowtime")}
                            placeholder={t(
                              "Cameras:placeholder_shortwindowtime",
                            )}
                            value={field.value?.toString()}
                            onChange={field.onChange}
                            validationError={
                              errors.shortWindowTime &&
                              t("Cameras:validation_shortwindowtime")
                            }
                          />
                        )}
                      />
                    </div>
                  </TwoColGrid>
                  <Spacer size={16} />
                </>
              )}
            </>
          )}
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextInput
                dataCy="description"
                label={t("Cameras:labels_description")}
                placeholder={t("Cameras:placeholder_description")}
                value={field.value}
                onChange={field.onChange}
                validationError={
                  errors.description && t("Cameras:validation_description")
                }
              />
            )}
          />
          {cameraTypeGroups.regular.includes(cameraType) && (
            <>
              <Spacer size={16} />
              <Divider />
              <Spacer size={16} />
              <TwoColGrid>
                <Controller
                  name="ssl"
                  control={control}
                  render={({ field }) => (
                    <Row style={{ justifyContent: "flex-start" }}>
                      <Checkbox
                        dataCy="ssl"
                        checked={field.value}
                        onChange={field.onChange}
                        style={{ padding: 0 }}
                        label={t("Cameras:inputs_ssl")}
                        disabled={cameraType === 7}
                      />
                    </Row>
                  )}
                />
              </TwoColGrid>
              <Spacer size={16} />
              <TwoColGrid columnGap={16}>
                <Controller
                  name="cameraAddress"
                  control={control}
                  rules={{ required: true, validate: validateIp as any }}
                  render={({ field }) => (
                    <TextInput
                      required
                      inputRef={field.ref}
                      dataCy="cameraAddress"
                      label={t("Cameras:labels_adress")}
                      placeholder={t("Cameras:placeholder_adress")}
                      value={field.value}
                      onChange={field.onChange}
                      validationError={
                        errors.cameraAddress &&
                        ((errors.cameraAddress.type === "required" &&
                          t("Cameras:validation_adress")) ||
                          t("Cameras:validation_ip_format"))
                      }
                    />
                  )}
                />
                <Controller
                  name="cameraPort"
                  control={control}
                  rules={{
                    required: true,
                    validate: () => Number(getValues("cameraPort")) > 0,
                  }}
                  render={({ field }) => (
                    <TextInput
                      required
                      inputRef={field.ref}
                      dataCy="cameraPort"
                      type="number"
                      label={t("Cameras:labels_port")}
                      placeholder={t("Cameras:placeholder_port")}
                      value={field.value?.toString()}
                      onChange={(e, val: string) => {
                        if (!isEmpty(val)) {
                          setValue(field.name, +val);
                        } else {
                          setValue(field.name, undefined);
                        }
                        trigger(field.name);
                      }}
                      validationError={
                        errors.cameraPort &&
                        ((errors.cameraPort.type === "required" &&
                          t("Cameras:validation_port")) ||
                          t("Cameras:validation_port_positive"))
                      }
                    />
                  )}
                />
              </TwoColGrid>
              {camera?.externalPort && (
                <>
                  <Spacer size={16} />
                  <Divider />
                  <Spacer size={16} />
                  <TextInput
                    dataCy="cameraExternalPort"
                    value={`${camera?.externalPort}`}
                    disabled
                    label={t("Cameras:labels_externalport")}
                  />
                </>
              )}
            </>
          )}
          {!isNew.current && (
            <>
              <Spacer size={32} />
              <Divider />
              <Spacer size={32} />
            </>
          )}
          {!isNew.current &&
            !cameraTypeGroups.regular.includes(cameraType) &&
            cameraStatus && (
              <RoleFeature requires={[Role.CameraForceProvisioning]}>
                <Column align="fill">
                  <h2>{t("Cameras:status_details_title")}</h2>
                  <p>{t("Cameras:status_details_description")}</p>
                  <Spacer size={16} />
                </Column>
                <Column type="fill" align="fill">
                  <Row>
                    <InputContainer
                      label={
                        <span className="labelRowLabel">
                          {t("Cameras:labels_external_configuration")}
                        </span>
                      }
                      input={
                        <ProvisioningStatus
                          provisioning={cameraStatus?.provisioning}
                        />
                      }
                    />
                  </Row>
                  <Spacer size={16} />
                  <DeviceStatus cameraStatus={cameraStatus} />
                  <Spacer size={16} />
                  <div>
                    <Button
                      loading={isRefreshingStatus}
                      variant="secondary"
                      image="refresh"
                      text={t("Cameras:buttons_refresh_connectivity")}
                      onClick={async () => {
                        setIsRefreshingStatus(true);
                        await loadStatus();
                        setIsRefreshingStatus(false);
                      }}
                    />
                  </div>
                </Column>
              </RoleFeature>
            )}
          {cameraTypeGroups.regular.includes(cameraType) && (
            <>
              <Column align="fill">
                <Spacer size={16} />
                <h2>{t("Cameras:advanced_details_title")}</h2>
                <p>{t("Cameras:advanced_details_description")}</p>
                <Spacer size={16} />
              </Column>
              <Row type="fill" align="fill">
                <Controller
                  name="mjpegPath"
                  control={control}
                  render={({ field }) => (
                    <TextInput
                      type="text"
                      label={
                        cameraType === 7
                          ? t("Cameras:labels_mjpeg").replace("MJPEG p", "P")
                          : t("Cameras:labels_mjpeg")
                      }
                      placeholder={t("Cameras:placeholder_mjpeg")}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </Row>
              <Spacer size={16} />
              <Row type="fill" align="fill">
                <Controller
                  name="username"
                  control={control}
                  render={({ field }) => (
                    <TextInput
                      type="text"
                      label={t("Common:username")}
                      placeholder={t("Cameras:placeholder_username")}
                      value={field.value ?? ""}
                      onChange={field.onChange}
                    />
                  )}
                />

                <Spacer size={16} />

                <Controller
                  name="password"
                  control={control}
                  rules={{
                    minLength: 1,
                  }}
                  render={({ field }) => (
                    <TextInput
                      inputRef={field.ref}
                      autoComplete="new-password"
                      type="password"
                      label={t("Cameras:labels_password")}
                      placeholder={t("Cameras:placeholder_password")}
                      value={field.value ?? ""}
                      onChange={field.onChange}
                      validationError={
                        errors.password && t("Errors:password_format")
                      }
                    />
                  )}
                />
              </Row>
              {!isNewCamera && (watch("password") || watch("username")) && (
                <>
                  <Spacer size={16} />
                  <Row type="fill" align="fill">
                    <Button
                      variant="link"
                      text={t("Cameras:buttons_clear_user_pass")}
                      onClick={() => {
                        setValue("username", null);
                        setValue("password", null);
                      }}
                    />
                  </Row>
                </>
              )}
            </>
          )}
        </Column>
      </form>
    );
  },
);
